import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Users } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export const ClientContacts = ({ client, formData }: { client: any, formData: any }) => {
    const { contacts, addContact, deleteContact, updateClient } = useDespachos();
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState("");
    const [role, setRole] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Obtener los contactos guardados localmente en client.address
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }
    const jsonContacts: any[] = extraData.contacts || [];

    // Filtrar contactos por la empresa/nombre del cliente
    const companyKey = formData.razonSocial || client.name;
    const dbClientContacts = contacts.filter(
        c => (c.company && c.company.trim().toLowerCase() === companyKey?.trim().toLowerCase()) ||
             (c.company && c.company.trim().toLowerCase() === client.name?.trim().toLowerCase())
    );

    // Fusionar contactos de BD y de JSON sin duplicados por ID o email
    const mergedContactsMap = new Map();
    dbClientContacts.forEach(c => mergedContactsMap.set(c.id || c.email || c.name, c));
    jsonContacts.forEach(c => {
        if (!mergedContactsMap.has(c.id || c.email || c.name)) {
            mergedContactsMap.set(c.id || c.email || c.name, c);
        }
    });
    const clientContacts = Array.from(mergedContactsMap.values());

    const handleCreateContact = async () => {
        if (!name.trim()) return;

        const newContact = {
            id: Math.random().toString(36).substring(2, 9),
            name: name.trim(),
            role: role.trim() || "Contacto",
            company: companyKey,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined
        };

        // 1. Guardar en BD via hook
        try {
            await addContact(newContact);
        } catch (e) {
            console.error("No se pudo guardar en despachos_contacts:", e);
        }

        // 2. Persistir en JSON dentro de client.address para garantizar persistencia 100%
        const updatedExtra = {
            ...extraData,
            contacts: [...jsonContacts, newContact]
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        // Limpiar form
        setName("");
        setRole("");
        setEmail("");
        setPhone("");
        setIsOpen(false);
    };

    const handleDeleteContact = async (contactId: string) => {
        try {
            await deleteContact(contactId);
        } catch (e) {}

        const updatedJsonContacts = jsonContacts.filter(c => c.id !== contactId);
        const updatedExtra = {
            ...extraData,
            contacts: updatedJsonContacts
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Contactos Asociados</CardTitle>
                    <p className="text-xs text-gray-400">Personas de contacto vinculadas a este cliente</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Agregar Contacto
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Nuevo Contacto Asociado</DialogTitle>
                            <p className="text-xs text-gray-400">Registra un nuevo contacto para {companyKey}</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Nombre Completo *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Diana Valencia" value={name} onChange={e => setName(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Cargo / Rol</Label>
                                <Input className="rounded-xl" placeholder="Ej: Representante Legal, Asistente..." value={role} onChange={e => setRole(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Correo Electrónico</Label>
                                <Input type="email" className="rounded-xl" placeholder="email@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input className="rounded-xl" placeholder="300 123 4567" value={phone} onChange={e => setPhone(e.target.value)} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreateContact} disabled={!name.trim()}>Guardar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {clientContacts.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Nombre</TableHead>
                                <TableHead className="font-semibold text-gray-500">Cargo</TableHead>
                                <TableHead className="font-semibold text-gray-500">Email</TableHead>
                                <TableHead className="font-semibold text-gray-500">Teléfono</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clientContacts.map((c) => (
                                <TableRow key={c.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">{c.name}</TableCell>
                                    <TableCell className="text-gray-500 text-xs font-semibold">{c.role || 'Contacto'}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">{c.email || '—'}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">{c.phone || '—'}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteContact(c.id)}
                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Users className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay contactos asociados a este cliente.</p>
                        <p className="text-xs text-gray-300 mt-1">Usa el botón superior para agregar un contacto.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
